"use client";
import { useRouter as usePagesRouter } from "next/compat/router";

import {
  useRouter as useAppRouter,
  usePathname,
  useSearchParams
} from "next/navigation";

type NavOptions = {
  scroll?: boolean;
  shallow?: boolean;
};

type QueryValue = string | Array<string> | undefined;

function buildUrlFromPathAndQuery(
  pathname: string,
  query: Record<string, any> = {}
) {
  const parameters = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        parameters.append(key, String(item));
      }
    } else {
      parameters.set(key, String(value));
    }
  }

  const qs = parameters.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const useCompatRouter = () => {
  const pagesRouter = usePagesRouter();
  const appRouter = useAppRouter();
  const pathname = usePathname();
  const searchParameters = useSearchParams();

  const getAppCurrentQuery = () => {
    if (!searchParameters) return {};
    return Object.fromEntries(searchParameters.entries());
  };

  const query: Record<string, QueryValue> = (() => {
    const result: Record<string, QueryValue> = {};
    if (!searchParameters) return result;

    for (const [key, value] of searchParameters.entries()) {
      // App Router never returns arrays, but we keep API compatible
      if (key in result) {
        const previous = result[key];
        result[key] = Array.isArray(previous)
          ? [...previous, value]
          : [previous as string, value];
      } else {
        result[key] = value;
      }
    }

    return result;
  })();

  const push = (
    pathname: string,
    query: Record<string, any> = {},
    options: NavOptions = {}
  ) => {
    if (pagesRouter) {
      const url = typeof pathname === "string" ? { pathname, query } : pathname;

      return pagesRouter.push(url, undefined, options);
    }

    if (options.shallow === true && typeof window !== "undefined") {
      const url = buildUrlFromPathAndQuery(window.location.pathname, query);
      window.history.pushState({}, "", url);
      if (options.scroll) {
        window.scrollTo({ top: 0 });
      }
      return;
    }

    const url = buildUrlFromPathAndQuery(pathname, {
      ...getAppCurrentQuery(),
      ...query
    });
    appRouter.push(url, { scroll: options.scroll ?? true });
  };

  const replace = (
    pathname: string,
    query: Record<string, any> = {},
    options: NavOptions = {}
  ) => {
    if (pagesRouter) {
      const url = typeof pathname === "string" ? { pathname, query } : pathname;

      return pagesRouter.push(url, undefined, options);
    }

    if (options.shallow === true && typeof window !== "undefined") {
      const url = buildUrlFromPathAndQuery(window.location.pathname, query);

      window.history.replaceState({}, "", url);
      if (options.scroll) {
        window.scrollTo({ top: 0 });
      }
      return;
    }

    const url = buildUrlFromPathAndQuery(pathname, {
      ...getAppCurrentQuery(),
      ...query
    });

    appRouter.replace(url, { scroll: options.scroll ?? true });
  };
  return {
    ...pagesRouter,
    ...appRouter,
    query,
    pathname,
    push,
    replace,
    locale: "fa",
    isAppRouter: Boolean(pagesRouter)
  };
};
